import { useLiveQuery } from "dexie-react-hooks";
import { useMemo } from "react";
import {
  applyFilters,
  compute503020,
  computeDashboardSummary,
  computeDebtPayoff,
  computeMonthCategorySummary,
  computeMonthlyBreakdown,
  computeSpendingCalendar,
  type BudgetFilters,
} from "../lib/calculations";
import { db } from "../lib/db";
import type { DebtResult, MonthlySummary, Transaction } from "../lib/schema";

export type { BudgetFilters };

export function useAllTransactions(filters: BudgetFilters = {}) {
  const all = useLiveQuery(() => db.transactions.orderBy("date").reverse().toArray(), []);
  return useMemo(() => applyFilters(all ?? [], filters), [all, filters]);
}

export function useCategories() {
  return useLiveQuery(() => db.categories.toArray(), []) ?? [];
}

export function useAccounts() {
  return useLiveQuery(() => db.accounts.toArray(), []) ?? [];
}

export function useDebts() {
  return useLiveQuery(() => db.debts.toArray(), []) ?? [];
}

export function useSinkingFunds() {
  return useLiveQuery(() => db.sinkingFunds.toArray(), []) ?? [];
}

export function useNetWorthLogs() {
  return useLiveQuery(() => db.netWorthLogs.orderBy("monthYear").toArray(), []) ?? [];
}

export function useBudgetSummary(year: number, person = "All") {
  const txs = useAllTransactions({ year, person });
  const categories = useCategories();
  return useMemo(() => computeDashboardSummary(txs, categories), [txs, categories]);
}

export function useMonthlyBreakdown(year: number, person = "All") {
  const txs = useAllTransactions({ year, person });
  return useMemo((): MonthlySummary[] => computeMonthlyBreakdown(txs, year), [txs, year]);
}

export function useMonthCategorySummary(year: number, month: number, person = "All") {
  const allTxs = useAllTransactions({ year, person });
  const categories = useCategories();
  return useMemo(
    () => computeMonthCategorySummary(allTxs, categories, year, month),
    [allTxs, categories, year, month]
  );
}

export function use503020(year: number, month: number, person = "All") {
  const allTxs = useAllTransactions({ year, person });
  const categories = useCategories();
  return useMemo(
    () => compute503020(allTxs, categories, year, month),
    [allTxs, categories, year, month]
  );
}

export function useSpendingCalendar(year: number, month: number, person = "All") {
  const allTxs = useAllTransactions({ year, person });
  return useMemo(() => {
    const { byDay, streak } = computeSpendingCalendar(allTxs, year, month);
    return { byDay, streak };
  }, [allTxs, year, month]);
}

export function useDebtCalculator(method: "snowball" | "avalanche" = "snowball") {
  const debts = useDebts();
  return useMemo((): DebtResult[] => computeDebtPayoff(debts, method), [debts, method]);
}

export function usePeopleOptions() {
  const txs = useLiveQuery(() => db.transactions.toArray(), []) ?? [];
  return useMemo(() => {
    const people = [...new Set(txs.map((t) => t.person))].filter(Boolean).sort();
    return ["All", ...people];
  }, [txs]);
}

export function useAvailableYears(): number[] {
  const txs = useLiveQuery(() => db.transactions.toArray(), []);
  return useMemo(() => {
    const current = new Date().getFullYear();
    const yearSet = new Set<number>();
    for (let y = current - 3; y <= current + 20; y++) yearSet.add(y);
    (txs ?? []).forEach((t) => {
      const y = parseInt(t.date.slice(0, 4), 10);
      if (!isNaN(y)) yearSet.add(y);
    });
    return Array.from(yearSet).sort((a, b) => a - b);
  }, [txs]);
}

export function useTransactionsByDate(date: string | null): Transaction[] {
  const txs = useLiveQuery<Transaction[]>(
    () => (date ? db.transactions.where("date").equals(date).toArray() : Promise.resolve<Transaction[]>([])),
    [date]
  );
  return txs ?? [];
}
